import axios from "axios";
import { BASE_PATH, getAccessToken } from "../api/api";
import { getData, deleteData, postData } from "../api/api";

export type IngestEnvironment = "STAGING" | "PRODUCTION";

export type CsvDelimiter = "AUTO" | "COMMA" | "SEMICOLON" | "TAB";

export type IngestJobResponse = {
  id: string;
  targetIndex: string;
  environment: IngestEnvironment;
  sourceFileName?: string;
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "PARTIALLY_COMPLETED" | "FAILED";
  rowCount: number;
  indexedCount: number;
  errorCount: number;
  bulkBatchSize?: number;
  progress?: string;
  errors?: string[];
  failedRecords?: IngestFailedRecord[];
  lineage?: Record<string, unknown>;
};

export type IngestFailedRecord = {
  rowNumber: number;
  column?: string;
  errorType?: string;
  errorReason?: string;
};

export const ingestService = {
  uploadFile: async (
    file: File,
    index: string,
    environment: IngestEnvironment = "STAGING",
    bulkBatchSize = 50,
    csvDelimiter: CsvDelimiter = "AUTO"
  ): Promise<IngestJobResponse> => {
    const form = new FormData();
    form.append("file", file);
    form.append("index", index);
    form.append("environment", environment);
    form.append("bulkBatchSize", String(bulkBatchSize));
    form.append("csvDelimiter", csvDelimiter);
    const token = await getAccessToken();
    const base = BASE_PATH.replace(/\/$/, "");
    const res = await axios.post(`${base}/ingest/file`, form, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "multipart/form-data",
      },
      timeout: 120_000,
    });
    return res.data as IngestJobResponse;
  },

  getJob: (id: string) =>
    getData(`/ingest/jobs/${id}`, { skipGlobalLoading: true }) as Promise<IngestJobResponse>,

  pollJobUntilFinished: async (
    id: string,
    options?: { intervalMs?: number; timeoutMs?: number }
  ): Promise<IngestJobResponse> => {
    const intervalMs = options?.intervalMs ?? 2000;
    const timeoutMs = options?.timeoutMs ?? 600_000;
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      const job = await ingestService.getJob(id);
      const status = String(job.status ?? "").toUpperCase();
      if (status !== "PROCESSING" && status !== "PENDING") {
        return job;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    throw new Error("Ingest job timed out while waiting for completion");
  },

  listJobs: () =>
    getData("/ingest/jobs", { skipGlobalLoading: true }) as Promise<IngestJobResponse[]>,

  deleteJob: (id: string) => deleteData(`/ingest/jobs/${id}`),

  promoteToProduction: (id: string) =>
    postData(`/ingest/jobs/${id}/promote`, {}) as Promise<IngestJobResponse>,

  analyzeForMatchingTask: (id: string) =>
    postData(`/ingest/jobs/${id}/analyze`, {}) as Promise<IngestJobResponse>,

  listIndices: () =>
    getData("/ingest/indices") as Promise<{ indices: string[] }>,
};
